import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import axios from 'axios';

const MaintenanceHistoryModal = ({ isOpen, onClose, truck, onNewMaintenance, onCompleteMaintenance, language = 'pt-BR' }) => {
  const [maintenances, setMaintenances] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen && truck) {
      loadMaintenances();
    }
  }, [isOpen, truck]);

  const loadMaintenances = async () => {
    setLoading(true);
    try {
      const token = sessionStorage.getItem('access_token');
      const API_URL = process.env.REACT_APP_BACKEND_URL;

      const response = await axios.get(
        `${API_URL}/api/trucks/${truck.id}/maintenance`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      setMaintenances(response.data);
    } catch (error) {
      console.error('Erro:', error);
      toast.error(language === 'pt-BR' ? 'Erro ao carregar manutenções' : 'Error loading maintenances');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    switch(status) {
      case 'agendada':
        return <Badge className="bg-yellow-500 text-white">📅 {language === 'pt-BR' ? 'Agendada' : 'Scheduled'}</Badge>;
      case 'em_andamento':
        return <Badge className="bg-orange-500 text-white">🔧 {language === 'pt-BR' ? 'Em Andamento' : 'In Progress'}</Badge>;
      case 'concluida':
        return <Badge className="bg-green-500 text-white">✅ {language === 'pt-BR' ? 'Concluída' : 'Completed'}</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

  const getTipoBadge = (tipo) => {
    switch(tipo) {
      case 'preventiva':
        return <Badge variant="outline" className="bg-blue-50 text-blue-700">🔧 {language === 'pt-BR' ? 'Preventiva' : 'Preventive'}</Badge>;
      case 'corretiva':
        return <Badge variant="outline" className="bg-yellow-50 text-yellow-700">⚙️ {language === 'pt-BR' ? 'Corretiva' : 'Corrective'}</Badge>;
      case 'emergencial':
        return <Badge variant="outline" className="bg-red-50 text-red-700">🚨 {language === 'pt-BR' ? 'Emergencial' : 'Emergency'}</Badge>;
      default:
        return <Badge>{tipo}</Badge>;
    }
  };

  if (!truck) return null;

  const activeMaintenance = maintenances.find(m => m.status !== 'concluida');
  const completedMaintenances = maintenances.filter(m => m.status === 'concluida');

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>🔧 {language === 'pt-BR' ? 'Manutenções' : 'Maintenance'} - {truck.modelo} ({truck.placa})</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Informações do Caminhão */}
          <div className="bg-blue-50 p-4 rounded-lg">
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <span className="text-gray-600">{language === 'pt-BR' ? 'Modelo:' : 'Model:'}</span>
                <p className="font-semibold">{truck.modelo}</p>
              </div>
              <div>
                <span className="text-gray-600">KM:</span>
                <p className="font-semibold">{truck.quilometragem_atual?.toLocaleString()}</p>
              </div>
              <div>
                <span className="text-gray-600">{language === 'pt-BR' ? 'Status:' : 'Status:'}</span>
                <p className="font-semibold capitalize">{truck.status}</p>
              </div>
              <div>
                <span className="text-gray-600">{language === 'pt-BR' ? 'Custo Total Manutenção:' : 'Total Maintenance Cost:'}</span>
                <p className="font-semibold text-orange-600">${truck.custo_manutencao_total?.toFixed(2) || '0.00'}</p>
              </div>
            </div>
          </div>

          {/* Botão Nova Manutenção */}
          <div className="flex justify-end">
            <Button onClick={() => { onClose(); onNewMaintenance(truck); }} className="bg-orange-600 hover:bg-orange-700">
              🔧 {language === 'pt-BR' ? 'Nova Manutenção' : 'New Maintenance'}
            </Button>
          </div>

          {/* Manutenção Ativa */}
          {activeMaintenance && (
            <div className="border-2 border-orange-500 rounded-lg p-4 bg-gradient-to-br from-orange-50 to-yellow-50">
              <div className="flex items-start justify-between mb-3">
                <h4 className="font-bold text-orange-900 text-lg flex items-center gap-2">
                  🔧 {language === 'pt-BR' ? 'Manutenção em Andamento' : 'Active Maintenance'}
                </h4>
                {getStatusBadge(activeMaintenance.status)}
              </div>
              
              {/* Descrição Destacada */}
              <div className="mb-3 bg-white p-3 rounded-lg border border-orange-200">
                <p className="text-xs text-orange-700 font-semibold mb-1">{language === 'pt-BR' ? '📝 Serviço:' : '📝 Service:'}</p>
                <p className="text-sm font-medium">{activeMaintenance.descricao}</p>
              </div>
              
              {/* Grid de Informações */}
              <div className="grid grid-cols-2 gap-3 mb-3">
                <div className="bg-white p-2 rounded border border-orange-100">
                  <p className="text-xs text-orange-700 font-semibold mb-1">{language === 'pt-BR' ? '🔧 Tipo' : '🔧 Type'}</p>
                  {getTipoBadge(activeMaintenance.tipo)}
                </div>
                
                <div className="bg-white p-2 rounded border border-orange-100">
                  <p className="text-xs text-orange-700 font-semibold mb-1">{language === 'pt-BR' ? '📅 Início' : '📅 Started'}</p>
                  <p className="text-sm font-semibold">{activeMaintenance.data_inicio}</p>
                </div>
                
                <div className="bg-white p-2 rounded border border-orange-100">
                  <p className="text-xs text-orange-700 font-semibold mb-1">{language === 'pt-BR' ? '🏁 Previsão' : '🏁 Expected'}</p>
                  <p className="text-sm font-semibold">{activeMaintenance.data_fim_prevista || (language === 'pt-BR' ? 'Não definida' : 'Not set')}</p>
                </div>
                
                <div className="bg-white p-2 rounded border border-orange-100">
                  <p className="text-xs text-orange-700 font-semibold mb-1">{language === 'pt-BR' ? '👷 Responsável' : '👷 Responsible'}</p>
                  <p className="text-sm font-semibold">{activeMaintenance.responsavel}</p>
                </div>
                
                <div className="bg-white p-2 rounded border border-orange-100">
                  <p className="text-xs text-orange-700 font-semibold mb-1">{language === 'pt-BR' ? '💰 Custo Estimado' : '💰 Est. Cost'}</p>
                  <p className="text-lg font-bold text-orange-600">${activeMaintenance.custo?.toFixed(2)}</p>
                </div>
                
                <div className="bg-white p-2 rounded border border-orange-100">
                  <p className="text-xs text-orange-700 font-semibold mb-1">{language === 'pt-BR' ? '📊 Status' : '📊 Status'}</p>
                  <p className="text-sm font-semibold capitalize">{activeMaintenance.status}</p>
                </div>
              </div>
              
              {/* Observações Iniciais */}
              {activeMaintenance.observacoes && activeMaintenance.observacoes.trim() !== '' && (
                <div className="bg-white p-3 rounded border border-orange-200 mb-3">
                  <p className="text-xs text-orange-700 font-semibold mb-1">{language === 'pt-BR' ? '📋 Observações Iniciais:' : '📋 Initial Notes:'}</p>
                  <p className="text-sm text-gray-700">{activeMaintenance.observacoes}</p>
                </div>
              )}
              
              {/* Dias Decorridos */}
              {(() => {
                const diasDecorridos = Math.ceil((new Date() - new Date(activeMaintenance.data_inicio)) / (1000 * 60 * 60 * 24));
                return (
                  <div className="bg-blue-50 p-2 rounded border border-blue-200 mb-3">
                    <p className="text-xs text-blue-700">
                      ⏱️ {diasDecorridos} {language === 'pt-BR' ? 'dia(s) desde o início' : 'day(s) since start'}
                    </p>
                  </div>
                );
              })()}

              <div className="mt-3 flex gap-2">
                <Button
                  onClick={() => { onClose(); onCompleteMaintenance(activeMaintenance, truck); }}
                  size="sm"
                  className="flex-1 bg-green-600 hover:bg-green-700"
                >
                  ✅ {language === 'pt-BR' ? 'Concluir Manutenção' : 'Complete Maintenance'}
                </Button>
              </div>
            </div>
          )}

          {/* Histórico */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-semibold">{language === 'pt-BR' ? '📜 Histórico Completo de Manutenções' : '📜 Complete Maintenance History'}</h4>
              <Badge variant="outline">{completedMaintenances.length} {language === 'pt-BR' ? 'concluídas' : 'completed'}</Badge>
            </div>
            
            {loading && <p className="text-center text-gray-500">{language === 'pt-BR' ? 'Carregando...' : 'Loading...'}</p>}
            
            {!loading && completedMaintenances.length === 0 && (
              <div className="text-center py-8 bg-gray-50 rounded-lg">
                <p className="text-4xl mb-2">🔧</p>
                <p className="text-gray-500">{language === 'pt-BR' ? 'Nenhuma manutenção concluída ainda' : 'No completed maintenances yet'}</p>
              </div>
            )}
            
            <div className="space-y-2">
              {completedMaintenances.map((maint) => {
                const dias = maint.data_fim_real && maint.data_inicio 
                  ? Math.ceil((new Date(maint.data_fim_real) - new Date(maint.data_inicio)) / (1000 * 60 * 60 * 24))
                  : null;
                
                return (
                  <div key={maint.id} className="border-2 rounded-lg p-4 bg-white hover:shadow-md transition-shadow">
                    {/* Cabeçalho */}
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex gap-2 items-center">
                        {getTipoBadge(maint.tipo)}
                        {getStatusBadge(maint.status)}
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-gray-500">{language === 'pt-BR' ? 'Registrada em:' : 'Registered:'}</p>
                        <p className="text-xs font-semibold">{new Date(maint.created_at).toLocaleDateString('en-US', { timeZone: 'America/New_York', month: '2-digit', day: '2-digit', year: 'numeric' })}</p>
                      </div>
                    </div>
                    
                    {/* Descrição */}
                    <div className="mb-3 bg-gray-50 p-3 rounded">
                      <p className="text-xs text-gray-500 mb-1">{language === 'pt-BR' ? '📝 Descrição:' : '📝 Description:'}</p>
                      <p className="text-sm font-medium">{maint.descricao}</p>
                    </div>
                    
                    {/* Detalhes em Grid */}
                    <div className="grid grid-cols-2 gap-3 mb-3">
                      {/* Período */}
                      <div className="bg-blue-50 p-2 rounded">
                        <p className="text-xs text-blue-700 font-semibold mb-1">{language === 'pt-BR' ? '📅 Período' : '📅 Period'}</p>
                        <p className="text-sm"><strong>{language === 'pt-BR' ? 'Início:' : 'Start:'}</strong> {maint.data_inicio}</p>
                        {maint.data_fim_prevista && (
                          <p className="text-sm"><strong>{language === 'pt-BR' ? 'Previsão:' : 'Expected:'}</strong> {maint.data_fim_prevista}</p>
                        )}
                        {maint.data_fim_real && (
                          <p className="text-sm text-green-700"><strong>{language === 'pt-BR' ? 'Concluída:' : 'Completed:'}</strong> {maint.data_fim_real}</p>
                        )}
                        {dias && (
                          <p className="text-xs text-gray-600 mt-1">⏱️ {dias} {language === 'pt-BR' ? 'dia(s)' : 'day(s)'}</p>
                        )}
                      </div>
                      
                      {/* Responsável */}
                      <div className="bg-purple-50 p-2 rounded">
                        <p className="text-xs text-purple-700 font-semibold mb-1">{language === 'pt-BR' ? '👷 Responsável' : '👷 Responsible'}</p>
                        <p className="text-sm font-medium">{maint.responsavel}</p>
                        <p className="text-xs text-gray-600 mt-1">{language === 'pt-BR' ? 'Registrado por:' : 'Registered by:'} {maint.created_by}</p>
                      </div>
                      
                      {/* Custos */}
                      <div className="bg-yellow-50 p-2 rounded">
                        <p className="text-xs text-yellow-700 font-semibold mb-1">{language === 'pt-BR' ? '💰 Custos' : '💰 Costs'}</p>
                        <p className="text-sm"><strong>{language === 'pt-BR' ? 'Estimado:' : 'Estimated:'}</strong> ${maint.custo?.toFixed(2)}</p>
                        {maint.status === 'concluida' && (
                          <p className="text-sm text-green-700"><strong>{language === 'pt-BR' ? 'Final:' : 'Final:'}</strong> ${maint.custo?.toFixed(2)}</p>
                        )}
                      </div>
                      
                      {/* Status Geral */}
                      <div className="bg-green-50 p-2 rounded">
                        <p className="text-xs text-green-700 font-semibold mb-1">{language === 'pt-BR' ? '📊 Status' : '📊 Status'}</p>
                        <p className="text-sm capitalize">{maint.status}</p>
                        {maint.invoice_url && (
                          <p className="text-xs text-blue-600 mt-1 cursor-pointer hover:underline">
                            📎 {language === 'pt-BR' ? 'Ver Comprovante' : 'View Invoice'}
                          </p>
                        )}
                      </div>
                    </div>
                    
                    {/* Observações */}
                    {maint.observacoes && maint.observacoes.trim() !== '' && (
                      <div className="bg-gray-50 p-3 rounded border-l-4 border-gray-400">
                        <p className="text-xs text-gray-500 mb-1">{language === 'pt-BR' ? '📋 Observações:' : '📋 Notes:'}</p>
                        <p className="text-sm text-gray-700">{maint.observacoes}</p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button onClick={onClose}>
            {language === 'pt-BR' ? 'Fechar' : 'Close'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default MaintenanceHistoryModal;